import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../../api/queryClient';
import { ServiceAgreementStatusEnum } from '../../api/generated/pm';
import {
  getV1CustomerByCustomerNumberOptions,
  getV1RequestBusinessPartnerProxiesQueryKey,
  getV1ServiceAgreementByIdOptions,
  getV1ServiceAgreementByIdQueryKey,
  getV1ServiceAgreementsWithCustomerOptions,
  getV1ServiceAgreementsWithCustomerQueryKey,
} from '../../api/generated/pm/@tanstack/react-query.gen';
import type { DivisionScopedListQueryParams } from '../../hooks/queries/useDivisionRollup';

/**
 * Query hooks for the customer side of Service Agreements: the Customers list
 * (agreements plus pending proxy requests), a single agreement, and the
 * Topcon-number customer lookup. See
 * `../Settings/IdentityProviders/identityProviders.queries.ts` for the pattern.
 */

export function useCustomerWithTopconNumber(topconNumber: string) {
  return useQuery({
    ...getV1CustomerByCustomerNumberOptions({ path: { customerNumber: topconNumber } }),
    enabled: topconNumber.length === 12,
  });
}

export function useServiceAgreementWithId(id?: string) {
  return useQuery({
    ...getV1ServiceAgreementByIdOptions({ path: { id: id as string } }),
    enabled: !!id,
  });
}

/**
 * Customers list (ticket 02): active Service Agreements with customers,
 * server-paged and scoped to the Division rollup (`useDivisionRollup`, PRD
 * D5/D6). Keeps the previous page's data while the next loads so paging and
 * filter changes don't flash the table (PRD 02 "Data").
 */
export function useCustomersList({
  page,
  limit,
  search,
  scope,
  selectedDivisionIds,
  enabled,
}: DivisionScopedListQueryParams) {
  return useQuery({
    ...getV1ServiceAgreementsWithCustomerOptions({
      query: {
        page,
        limit,
        'filter[serviceAgreementStatus]': { in: ServiceAgreementStatusEnum.ACTIVE },
        scope,
        ...(search && { 'filter[customer.name]': { startswith: search } }),
        ...(selectedDivisionIds.length && {
          'filter[providerBusinessPartnerId]': { in: selectedDivisionIds.join(',') },
        }),
      },
    }),
    enabled,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Prefix shared by every `getV1ServiceAgreementById` cache entry. Pass this to
 * `invalidateQueries` to clear all cached agreements regardless of id.
 */
export const SERVICE_AGREEMENT_QUERY_KEY = (() => {
  const [{ _id, baseUrl }] = getV1ServiceAgreementByIdQueryKey({ path: { id: '' } });
  return [{ _id, baseUrl }];
})();

/**
 * Clears one cached Service Agreement, or every cached agreement when called
 * without an id (the generated key requires a path, so the prefix is derived
 * by dropping it).
 */
export const invalidateServiceAgreementWithId = (id?: string) => {
  if (id) {
    return queryClient.invalidateQueries({
      queryKey: getV1ServiceAgreementByIdQueryKey({ path: { id } }),
    });
  }
  return queryClient.invalidateQueries({ queryKey: SERVICE_AGREEMENT_QUERY_KEY });
};

/**
 * Invalidate every cached Customers / Service Agreements list: every
 * `getV1ServiceAgreementsWithCustomer` query and every
 * `getV1RequestBusinessPartnerProxies` query, regardless of their params
 * (page, filter, scope, …). Calling the generated key function without
 * options returns the id-only prefix, which `invalidateQueries` matches
 * against every cached variant — the same technique as
 * `invalidateServiceAgreementWithId()`.
 *
 * Note: when review runs in a separate tab (`window.open`), this only clears
 * that tab's QueryClient. The list tab relies on its visibility refetch.
 */
export const invalidateCustomerAgreementLists = () =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: getV1ServiceAgreementsWithCustomerQueryKey() }),
    queryClient.invalidateQueries({ queryKey: getV1RequestBusinessPartnerProxiesQueryKey() }),
  ]);
